import { Alert, Dialog, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProofButton } from '../../../ui/button/ProofButton';
import { ApplicantionFromToken } from '../../../models/doc-upload.model';
import { contactSupport } from '../../../services/plaid.service';

interface Props {
  userDetails: ApplicantionFromToken;
  onClose: VoidFunction;
}

export default function SendMessageModal({ userDetails, onClose }: Props) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  const handleSendMessage = async () => {
    setSending(true);
    setSendFailed(false);
    const result = await contactSupport({
      message,
      applicant_id: userDetails.applicant_id,
      // Top-level, not userDetails.application: the API trims `application` and may omit it.
      application_id: userDetails.application_id,
    });
    setSending(false);

    // Only close as "sent" when it was; otherwise keep the message so the user can retry.
    if (result.status === 'success') {
      onClose();
    } else {
      setSendFailed(true);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ px: 2 }} variant="h5">
        {t('verification.cosign.message.title')}
      </DialogTitle>
      <DialogContent sx={{ px: 2, pb: 2 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('verification.cosign.message.desc')}
        </Typography>
        <TextField
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder={t('verification.cosign.message.placeholder')}
          multiline
          rows={5}
          fullWidth
          InputProps={{ sx: { height: '152px' } }}
          sx={{ pb: 2 }}
        />
        {sendFailed && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('verification.cosign.message.error')}
          </Alert>
        )}
        <ProofButton disabled={!message || sending} variant="contained" fullWidth onClick={handleSendMessage}>
          {t('verification.cosign.message.button')}
        </ProofButton>
      </DialogContent>
    </Dialog>
  );
}
